import {
  PQ_KEY_PREFIX,
  credentialScope,
  hpkeSeal,
  parsePublicKey,
  sealDestinationProof,
} from "../shared/credentials/hpkeFormat";
import {
  canBindCredential,
  credentialPolicy,
  type CredentialPolicyConnector,
} from "../shared/credentials/credentialPolicy";
import { applyConfigurationPatch } from "../shared/connectors/configurationPatch";
import { normalizeConnectorUrlPattern } from "../shared/connectors/normalizeUrlPattern";
import type { z } from "zod";
import type { updateConnectorSchema } from "../shared/mutators/updateConnectedApp";
import { config } from "./environment";
import { emitErrorToast } from "./lib/errorToast";

let sealingKey: string | null | undefined;
let requirePostQuantum = false;

type SealingConnector = CredentialPolicyConnector & { id: string };
type EditProof = { core: string; key: Uint8Array };
const activeEdits = new Set<{
  tenantId: string;
  connectorId: string;
  proofs: Map<string, EditProof>;
  fieldEpochs: Map<string, number>;
  writes: Promise<void>;
  pendingWrites: number;
}>();

export function beginConnectorDestinationEdit(
  tenantId: string,
  connectorId: string
) {
  const edit = {
    tenantId,
    connectorId,
    proofs: new Map<string, EditProof>(),
    fieldEpochs: new Map<string, number>(),
    writes: Promise.resolve(),
    pendingWrites: 0,
  };
  activeEdits.add(edit);
  return () => {
    edit.proofs.clear();
    activeEdits.delete(edit);
  };
}

function activeEdit(tenantId: string, connectorId: string) {
  return [...activeEdits].findLast(
    (edit) => edit.tenantId === tenantId && edit.connectorId === connectorId
  );
}

function editProofs(tenantId: string, connectorId: string) {
  return activeEdit(tenantId, connectorId)?.proofs;
}

export function queueConnectorEdit<T>(
  tenantId: string,
  connectorId: string,
  write: () => Promise<T> | T
): Promise<T> {
  const edit = activeEdit(tenantId, connectorId);
  if (!edit) return Promise.resolve(write());
  edit.pendingWrites++;
  const result = edit.writes.then(write);
  edit.writes = result.then(
    () => {
      edit.pendingWrites--;
    },
    () => {
      edit.pendingWrites--;
    }
  );
  return result;
}

export function waitForConnectorEdits(
  tenantId: string,
  connectorId: string
): Promise<void> | undefined {
  const edit = activeEdit(tenantId, connectorId);
  if (!edit?.pendingWrites) return;
  return (async () => {
    let writes: Promise<void>;
    do {
      writes = edit.writes;
      await writes;
    } while (writes !== edit.writes);
  })();
}

export function forgetConnectorDestinationProof(
  tenantId: string,
  connectorId: string,
  field?: string
): void {
  const edit = activeEdit(tenantId, connectorId);
  if (!edit) return;
  if (field === undefined) {
    edit.proofs = new Map();
    edit.fieldEpochs.clear();
  } else {
    edit.proofs.delete(field);
    edit.fieldEpochs.set(field, (edit.fieldEpochs.get(field) ?? 0) + 1);
  }
}

type ConnectorUpdate = z.infer<typeof updateConnectorSchema>;

export function withConnectorDestinationProof(
  app: SealingConnector & { name: string },
  changes: Partial<ConnectorUpdate>
): ConnectorUpdate | Promise<ConnectorUpdate> {
  const args = {
    connectedAppId: app.id,
    ...changes,
  };
  const proofs = editProofs(app.tenantId, app.id);
  if (!canBindCredential(app) || !proofs?.size) return args;
  const identifier = (args.identifier ?? app.identifier).trim();
  const configuration = applyConfigurationPatch(
    args.configuration ?? app.configuration,
    args.configurationPatch
  );
  const nextApp = {
    ...app,
    identifier,
    configuration,
    urlPattern: normalizeConnectorUrlPattern(
      args.urlPattern ?? app.urlPattern,
      identifier,
      configuration,
      app.configuration
    ),
  };
  const changedProofs: Array<[string, EditProof, string]> = [];
  for (const [field, entry] of proofs) {
    if (Object.hasOwn(args.secrets ?? {}, field)) continue;
    const previous = credentialPolicy(app, field);
    const next = credentialPolicy(nextApp, field);
    if (previous === next) continue;
    changedProofs.push([field, entry, next]);
  }
  if (changedProofs.length === 0) return args;
  return Promise.all(
    changedProofs.map(async ([field, entry, next]) => [
      field,
      await sealDestinationProof(entry.core, next, Date.now(), entry.key),
    ])
  ).then((newProofs) => ({
    ...args,
    destinationProofs: {
      ...args.destinationProofs,
      ...Object.fromEntries(newProofs),
    },
  }));
}

class SealingRefusedError extends Error {
  constructor() {
    super("refusing to store a connector secret in plaintext");
    this.name = "SealingRefusedError";
  }
}

export function setSealingKey(
  key: string | null | undefined,
  postQuantum = false
) {
  sealingKey = key;
  requirePostQuantum = postQuantum;
}

export async function sealSecret(
  plaintext: string,
  binding?: { app: SealingConnector; field: string },
  isCurrent?: () => boolean
): Promise<string> {
  if (sealingKey === undefined) {
    throw new Error("sealing a secret before SignedIn loaded the tenant key");
  }
  if (sealingKey === null) {
    if (config.credentialSealingRequired || requirePostQuantum) {
      emitErrorToast({
        icon: "lock",
        message: "Failed to save the secret. Please reload and try again.",
        buttonLabel: "Dismiss",
      });
      throw new SealingRefusedError();
    }
    return plaintext;
  }
  if (requirePostQuantum && !sealingKey.startsWith(PQ_KEY_PREFIX))
    throw new SealingRefusedError();
  const bound =
    binding &&
    sealingKey.startsWith(PQ_KEY_PREFIX) &&
    canBindCredential(binding.app)
      ? binding
      : undefined;
  const edit = bound && activeEdit(bound.app.tenantId, bound.app.id);
  const proofs = edit?.proofs;
  const fieldEpoch = bound && (edit?.fieldEpochs.get(bound.field) ?? 0);
  const scope = bound && credentialScope(bound.app.tenantId, bound.field);
  const policy = bound && credentialPolicy(bound.app, bound.field);
  const issuedAt = Date.now();
  const { ciphertext, editKey } = await hpkeSeal(
    plaintext,
    await parsePublicKey(sealingKey),
    scope && policy ? { scope, policy, issuedAt } : undefined
  );
  if (
    bound &&
    editKey &&
    proofs &&
    (isCurrent?.() ?? true) &&
    proofs === editProofs(bound.app.tenantId, bound.app.id) &&
    fieldEpoch === (edit?.fieldEpochs.get(bound.field) ?? 0)
  ) {
    proofs.set(bound.field, {
      core: ciphertext.split("$").slice(0, 8).join("$"),
      key: editKey,
    });
  }
  return ciphertext;
}

export async function trySealSecret(
  plaintext: string,
  binding?: { app: SealingConnector; field: string },
  isCurrent?: () => boolean
): Promise<string | undefined> {
  try {
    return await sealSecret(plaintext, binding, isCurrent);
  } catch (error) {
    if (error instanceof SealingRefusedError) return undefined;
    throw error;
  }
}
